import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
   createExamQuestion,
   getExamTickets,
   updateExamTicket,
} from "../../api/examAdminService";

const MIN_ANSWERS = 2;
const TICKETS_PAGE_LIMIT = 100;

function safeList(value) {
   return Array.isArray(value) ? value : [];
}

function isObject(value) {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function toPositiveInt(value) {
   const n = Number(value);
   return Number.isInteger(n) && n > 0 ? n : null;
}

function uniqPositiveInts(values) {
   return Array.from(
      new Set(
         safeList(values)
            .map((x) => Number(x))
            .filter((x) => Number.isInteger(x) && x > 0),
      ),
   );
}

function firstNonEmpty(...values) {
   for (const value of values) {
      const text = String(value ?? "").trim();
      if (text) return text;
   }
   return "";
}

function normalizeLooseKey(value) {
   return String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
}

function getValueByLooseKeys(obj, keys = []) {
   if (!isObject(obj)) return undefined;
   const normalized = keys.map((k) => normalizeLooseKey(k));

   for (const [key, value] of Object.entries(obj)) {
      if (normalized.includes(normalizeLooseKey(key))) {
         return value;
      }
   }

   return undefined;
}

function inferCorrectAnswerFromAnswerFlags(answersBase = []) {
   for (let i = 0; i < answersBase.length; i += 1) {
      const answer = answersBase[i];
      if (!isObject(answer)) continue;

      const flag =
         answer.correct ??
         answer.isCorrect ??
         answer.right ??
         answer.ok ??
         answer.valid ??
         false;

      if (flag === true) return i + 1;
   }

   return null;
}

function readItems(raw, fallbackKeys = []) {
   if (Array.isArray(raw)) return raw;
   if (!isObject(raw)) return [];

   const keys = [
      "data",
      "items",
      "results",
      "rows",
      "tickets",
      ...fallbackKeys,
   ];

   for (const key of keys) {
      const value = raw?.[key];
      if (Array.isArray(value)) return value;
   }

   return [];
}

function normalizeTicket(item, fallbackIndex = 0) {
   if (!isObject(item)) return null;

   const id = toPositiveInt(item?.id);
   if (!id) return null;

   const name = String(item?.name ?? `Ticket ${fallbackIndex + 1}`).trim();
   const type =
      String(item?.type ?? "EXAM")
         .trim()
         .toUpperCase() || "EXAM";

   const count =
      toPositiveInt(
         item?._count?.questions ?? item?.questionCount ?? item?.questionsCount,
      ) ?? safeList(item?.questionIds).length;

   return {
      id,
      localId: `ticket-${id}`,
      name,
      type,
      questionCount: count,
   };
}

function normalizeTicketList(items) {
   return safeList(items)
      .map((item, idx) => normalizeTicket(item, idx))
      .filter(Boolean)
      .sort((a, b) => Number(a.id) - Number(b.id));
}

function parseJsonArraySafe(value, fallback = []) {
   if (Array.isArray(value)) return value;
   if (typeof value !== "string") return fallback;

   const text = String(value ?? "").trim();
   if (!text) return fallback;

   try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed : fallback;
   } catch {
      return fallback;
   }
}

function parseLocalizedTextRaw(value) {
   if (isObject(value)) {
      return {
         ro: firstNonEmpty(value?.ro, value?.RO, value?.textRo, value?.text_ro),
         ru: firstNonEmpty(value?.ru, value?.RU, value?.textRu, value?.text_ru),
      };
   }

   const text = String(value ?? "").trim();
   if (!text) return { ro: "", ru: "" };

   try {
      const parsed = JSON.parse(text);
      if (isObject(parsed)) return parseLocalizedTextRaw(parsed);
   } catch {}

   return { ro: text, ru: "" };
}

function parseLocalizedAnswerRaw(value) {
   if (isObject(value)) {
      return {
         ro: firstNonEmpty(value?.ro, value?.RO),
         ru: firstNonEmpty(value?.ru, value?.RU),
      };
   }

   const text = String(value ?? "").trim();
   if (!text) return { ro: "", ru: "" };

   try {
      const parsed = JSON.parse(text);
      if (isObject(parsed)) return parseLocalizedAnswerRaw(parsed);
   } catch {}

   return { ro: text, ru: "" };
}

function stringifyLocalizedAnswer(answer) {
   const ro = firstNonEmpty(answer?.ro, answer?.ru);
   const ru = firstNonEmpty(answer?.ru, answer?.ro);

   return JSON.stringify({
      ro,
      ru,
   });
}

function normalizeImportCorrectAnswer(rawValue, answersCount) {
   const text = String(rawValue ?? "").trim();
   if (!text) return null;

   let n = null;

   if (/^[+-]?\d+$/.test(text)) {
      n = Number(text);
   } else {
      const upper = text.toUpperCase();

      if (/^[A-F]$/.test(upper)) {
         n = upper.charCodeAt(0) - 64;
      } else {
         const romanMap = {
            I: 1,
            II: 2,
            III: 3,
            IV: 4,
            V: 5,
            VI: 6,
         };
         if (Object.prototype.hasOwnProperty.call(romanMap, upper)) {
            n = romanMap[upper];
         } else {
            const nums = text.match(/\d+/g);
            if (nums?.length === 1) {
               n = Number(nums[0]);
            }
         }
      }
   }

   if (!Number.isInteger(n)) return null;
   if (n >= 1 && n <= answersCount) return n; // one-based
   if (n >= 0 && n < answersCount) return n + 1; // zero-based -> one-based
   return null;
}

function buildExamImagePath(ticketNo, orderNo) {
   const ticket = toPositiveInt(ticketNo);
   const order = toPositiveInt(orderNo);
   if (!ticket || !order) return null;
   return `/EXAMEN_INSTRUIRE_AUTO/images/B${ticket}/image${order}.jpeg`;
}

function guessTicketNumberFromName(value) {
   const text = String(value ?? "").trim();
   if (!text) return null;

   const m1 = text.match(/\bB(?:ilet)?\s*#?\s*(\d+)\b/i);
   if (m1) return toPositiveInt(m1[1]);

   const m2 = text.match(/\bBiletul\s*#?\s*(\d+)\b/i);
   if (m2) return toPositiveInt(m2[1]);

   const m3 = text.match(/\b(\d+)\b/);
   if (m3) return toPositiveInt(m3[1]);

   return null;
}

function normalizeImportQuestion(raw, index) {
   if (!isObject(raw)) {
      throw new Error(`Question #${index + 1} is not an object.`);
   }

   const sourceId = toPositiveInt(raw?.id) ?? index + 1;
   const order = toPositiveInt(raw?.order) ?? index + 1;

   const textLocalized = parseLocalizedTextRaw(raw?.text);
   const textRo = firstNonEmpty(
      raw?.textRo,
      raw?.text_ro,
      raw?.textRO,
      textLocalized.ro,
   );
   const textRu = firstNonEmpty(
      raw?.textRu,
      raw?.text_ru,
      raw?.textRU,
      textLocalized.ru,
   );

   if (!textRo && !textRu) {
      throw new Error(`Question #${order}: text/textRu missing.`);
   }

   const answersBase = parseJsonArraySafe(raw?.answers, []);
   const answersRuBase = parseJsonArraySafe(
      raw?.answersRu ?? raw?.answersRU ?? raw?.answers_ru,
      [],
   );

   if (!answersBase.length && !answersRuBase.length) {
      throw new Error(`Question #${order}: answers missing.`);
   }

   const maxAnswerSource = Math.max(answersBase.length, answersRuBase.length);
   const answers = [];

   for (let i = 0; i < maxAnswerSource; i += 1) {
      const roObj = parseLocalizedAnswerRaw(answersBase[i]);
      const ruObj = parseLocalizedAnswerRaw(answersRuBase[i]);

      const ro = firstNonEmpty(roObj?.ro, roObj?.ru);
      const ru = firstNonEmpty(ruObj?.ru, ruObj?.ro, roObj?.ru);

      if (!ro && !ru) continue;

      answers.push({
         ro: ro || ru,
         ru: ru || ro,
      });
   }

   if (answers.length < MIN_ANSWERS) {
      throw new Error(
         `Question #${order}: minimum ${MIN_ANSWERS} answers required.`,
      );
   }

   const rawCorrectAnswerFromKeys =
      getValueByLooseKeys(raw, [
         "correctAnswer",
         "correct_answer",
         "correctIndex",
         "correct_index",
         "rightIndex",
         "right_index",
         "answerIndex",
         "answer_index",
         "rightAnswer",
         "right_answer",
         "goodAnswer",
         "good_answer",
         "solution",
         "answer",
      ]) ?? null;

   const rawCorrectAnswerFromFlags =
      inferCorrectAnswerFromAnswerFlags(answersBase);

   const rawCorrectAnswer =
      rawCorrectAnswerFromKeys ??
      rawCorrectAnswerFromFlags ??
      getValueByLooseKeys(raw, ["selectedAnswer", "selected_answer"]) ??
      null;

   const parsedCorrectAnswer = normalizeImportCorrectAnswer(
      rawCorrectAnswer,
      answers.length,
   );
   const correctAnswer = parsedCorrectAnswer || 1;
   const correctAnswerFallbackUsed = !parsedCorrectAnswer;

   const categoryId =
      raw?.categoryId == null ? null : (toPositiveInt(raw?.categoryId) ?? null);

   return {
      source: {
         id: sourceId,
         order,
         text: textRo,
         textRu,
         image: firstNonEmpty(raw?.image) || null,
         answers: answers.map((a) => a.ro),
         answersRu: answers.map((a) => a.ru),
         correctAnswer: rawCorrectAnswer,
         correctAnswerFallbackUsed,
         categoryId: raw?.categoryId ?? null,
      },
      payload: {
         order,
         text: JSON.stringify({
            ro: textRo,
            ru: textRu,
         }),
         answers: answers.map((answer) => stringifyLocalizedAnswer(answer)),
         correctAnswer,
         categoryId,
      },
   };
}

function parseImportFileStrict(parsed) {
   const root = isObject(parsed) ? parsed : {};

   const rootItems = Array.isArray(parsed)
      ? parsed
      : parseJsonArraySafe(root?.questions, null) ||
        parseJsonArraySafe(root?.items, null) ||
        parseJsonArraySafe(root?.rows, null) ||
        parseJsonArraySafe(root?.data?.questions, null) ||
        parseJsonArraySafe(root?.data?.items, null);

   if (!Array.isArray(rootItems)) {
      throw new Error(
         'Invalid JSON. Expected array or object with "questions".',
      );
   }

   const items = rootItems
      .map((item, idx) => normalizeImportQuestion(item, idx))
      .sort((a, b) => Number(a.payload.order) - Number(b.payload.order));

   const usedOrders = new Set();

   const normalizedUnique = items.map((item, idx) => {
      const next = {
         ...item,
         source: { ...item.source },
         payload: { ...item.payload },
      };

      let nextOrder = toPositiveInt(next?.payload?.order);
      if (!nextOrder || usedOrders.has(nextOrder)) {
         nextOrder = idx + 1;
      }

      usedOrders.add(nextOrder);
      next.payload.order = nextOrder;
      next.source.order = nextOrder;

      return next;
   });

   return {
      items: normalizedUnique,
      correctFallbackCount: normalizedUnique.filter(
         (item) => item?.source?.correctAnswerFallbackUsed,
      ).length,
   };
}

function extractCreatedQuestionId(raw) {
   const direct = toPositiveInt(raw?.id);
   if (direct) return direct;

   const nestedQuestion = toPositiveInt(raw?.question?.id);
   if (nestedQuestion) return nestedQuestion;

   const dataId = toPositiveInt(raw?.data?.id);
   if (dataId) return dataId;

   const dataQuestionId = toPositiveInt(raw?.data?.question?.id);
   if (dataQuestionId) return dataQuestionId;

   return null;
}

async function fetchAllExamTicketsNoPagination() {
   const allRows = [];
   let page = 1;
   let guard = 0;

   while (guard < 500) {
      const res = await getExamTickets({
         page,
         limit: TICKETS_PAGE_LIMIT,
         type: "EXAM",
      });

      const rows = normalizeTicketList(readItems(res, ["tickets"]));
      allRows.push(...rows);

      const totalPages = Number(res?.pagination?.totalPages);
      if (Number.isInteger(totalPages) && totalPages > 0) {
         if (page >= totalPages) break;
      } else if (rows.length < TICKETS_PAGE_LIMIT) {
         break;
      }

      page += 1;
      guard += 1;
   }

   const map = new Map();
   for (const row of allRows) {
      if (!row?.id || map.has(row.id)) continue;
      map.set(row.id, row);
   }

   return Array.from(map.values()).sort((a, b) => Number(a.id) - Number(b.id));
}

export default function ExamTicketsMonitor() {
   const [alert, setAlert] = useState(null);

   const [ticketRows, setTicketRows] = useState([]);
   const [ticketLoading, setTicketLoading] = useState(false);
   const [ticketSearch, setTicketSearch] = useState("");
   const [selectedTicketId, setSelectedTicketId] = useState(null);

   const [importFileName, setImportFileName] = useState("");
   const [importItems, setImportItems] = useState([]);

   const [createLoading, setCreateLoading] = useState(false);
   const [createFailures, setCreateFailures] = useState([]);
   const [createdNowIds, setCreatedNowIds] = useState([]);
   const [syncedQuestionIds, setSyncedQuestionIds] = useState([]);

   const pushAlert = useCallback((message, type = "info") => {
      setAlert({
         id: Date.now(),
         type,
         message: String(message ?? ""),
      });
   }, []);

   useEffect(() => {
      if (!alert) return undefined;
      const timer = window.setTimeout(() => setAlert(null), 6500);
      return () => window.clearTimeout(timer);
   }, [alert]);

   const loadTickets = useCallback(async () => {
      try {
         setTicketLoading(true);
         const rows = await fetchAllExamTicketsNoPagination();
         setTicketRows(rows);

         if (!rows.length) {
            setSelectedTicketId(null);
            return;
         }

         setSelectedTicketId((prev) => {
            const hasPrev = rows.some((row) => Number(row.id) === Number(prev));
            return hasPrev ? prev : rows[0].id;
         });
      } catch (err) {
         pushAlert(
            `Could not load tickets: ${err?.message || "error"}`,
            "error",
         );
      } finally {
         setTicketLoading(false);
      }
   }, [pushAlert]);

   useEffect(() => {
      loadTickets();
   }, [loadTickets]);

   const filteredTickets = useMemo(() => {
      const q = ticketSearch.trim().toLowerCase();
      if (!q) return ticketRows;

      return ticketRows.filter((ticket) => {
         const hay = `${ticket.id} ${ticket.name} ${ticket.type}`.toLowerCase();
         return hay.includes(q);
      });
   }, [ticketRows, ticketSearch]);

   const selectedTicket = useMemo(() => {
      return (
         ticketRows.find(
            (ticket) => Number(ticket.id) === Number(selectedTicketId),
         ) || null
      );
   }, [ticketRows, selectedTicketId]);

   const ticketNoForImages = useMemo(() => {
      return (
         guessTicketNumberFromName(selectedTicket?.name) ||
         toPositiveInt(selectedTicket?.id)
      );
   }, [selectedTicket?.id, selectedTicket?.name]);

   const debugPreview = useMemo(() => {
      return importItems.map((item) => {
         const order = item?.payload?.order;
         const image = buildExamImagePath(ticketNoForImages, order);

         return {
            order,
            extracted: {
               text: item?.source?.text,
               textRu: item?.source?.textRu,
               image: item?.source?.image,
               answers: item?.source?.answers,
               answersRu: item?.source?.answersRu,
               correctAnswer: item?.source?.correctAnswer,
               correctAnswerFallbackUsed:
                  item?.source?.correctAnswerFallbackUsed,
               categoryId: item?.source?.categoryId,
            },
            payload: {
               text: item?.payload?.text,
               image,
               answers: item?.payload?.answers,
               correctAnswer: item?.payload?.correctAnswer,
               categoryId: item?.payload?.categoryId,
            },
         };
      });
   }, [importItems, ticketNoForImages]);

   const handleImportFileChange = useCallback(
      async (event) => {
         const file = event.target.files?.[0];
         if (!file) return;

         try {
            const rawText = await file.text();
            const sanitized = String(rawText ?? "").replace(/^\uFEFF/, "");

            let parsed = null;

            try {
               parsed = JSON.parse(sanitized);
            } catch {
               const noTrailingCommas = sanitized.replace(/,\s*([}\]])/g, "$1");
               parsed = JSON.parse(noTrailingCommas);
            }

            const normalized = parseImportFileStrict(parsed);

            setImportFileName(file.name);
            setImportItems(normalized.items);
            setCreateFailures([]);
            setCreatedNowIds([]);
            setSyncedQuestionIds([]);

            pushAlert(
               `File loaded: ${file.name}. Questions: ${normalized.items.length}. correctAnswer fallback: ${normalized.correctFallbackCount}.`,
               normalized.correctFallbackCount ? "warning" : "success",
            );
         } catch (err) {

            setImportFileName("");
            setImportItems([]);
            setCreateFailures([]);
            setCreatedNowIds([]);
            setSyncedQuestionIds([]);

            pushAlert(err?.message || "Invalid JSON import file.", "error");
         } finally {
            if (event?.target) event.target.value = "";
         }
      },
      [pushAlert],
   );

   const handleCreateAllAndSync = useCallback(async () => {
      if (!selectedTicket?.id) {
         pushAlert("Select a ticket first.", "warning");
         return;
      }

      if (!importItems.length) {
         pushAlert("Import a questions file first.", "warning");
         return;
      }

      try {
         setCreateLoading(true);
         setCreateFailures([]);
         setCreatedNowIds([]);
         setSyncedQuestionIds([]);

         const createdIds = [];
         const failures = [];

         for (const item of importItems) {
            const order = Number(item?.payload?.order ?? "?");
            const imagePath = buildExamImagePath(ticketNoForImages, order);

            try {
               if (!imagePath) {
                  throw new Error(
                     `Could not resolve image path for order ${order}.`,
                  );
               }


               const raw = await createExamQuestion({
                  text: item?.payload?.text,
                  image: imagePath,
                  answers: safeList(item?.payload?.answers).map((answer) =>
                     String(answer ?? ""),
                  ),
                  correctAnswer: item?.payload?.correctAnswer,
                  categoryId: item?.payload?.categoryId,
               });

               const qid = extractCreatedQuestionId(raw);
               if (!qid) {
                  throw new Error(
                     `Could not read created id for order ${order}.`,
                  );
               }

               createdIds.push(qid);
            } catch (err) {
               failures.push({
                  order,
                  message: err?.message || "Unknown API error",
               });
            }
         }

         const uniqCreated = uniqPositiveInts(createdIds);

         if (!uniqCreated.length) {
            throw new Error("No questions were created.");
         }

         await updateExamTicket(selectedTicket.id, {
            name:
               String(selectedTicket?.name ?? "").trim() ||
               `Ticket ${selectedTicket.id}`,
            questionIds: uniqCreated,
         });

         setCreatedNowIds(uniqCreated);
         setSyncedQuestionIds(uniqCreated);
         setCreateFailures(failures);

         if (failures.length) {
            pushAlert(
               `Created ${uniqCreated.length}/${importItems.length}. Synced exact created IDs to ticket. Failed: ${failures.length}.`,
               "warning",
            );
         } else {
            pushAlert(
               `Create + sync complete. Created and synced: ${uniqCreated.length}.`,
               "success",
            );
         }

         await loadTickets();
      } catch (err) {
         pushAlert(
            err?.message || "Could not create and sync questions.",
            "error",
         );
      } finally {
         setCreateLoading(false);
      }
   }, [importItems, loadTickets, pushAlert, selectedTicket, ticketNoForImages]);

   return (
      <section className="exam-admin-page">
         <div className="exam-admin">
            {alert ? (
               <div className="exam-admin__pager">
                  <span>
                     <strong>
                        {String(alert.type || "INFO").toUpperCase()}
                     </strong>
                     : {alert.message}
                  </span>
                  <button
                     type="button"
                     className="exam-admin__action-btn"
                     onClick={() => setAlert(null)}
                  >
                     Close
                  </button>
               </div>
            ) : null}

            <div className="exam-admin__layout exam-admin__layout--tickets">
               <article className="exam-admin__panel">
                  <div className="exam-admin__panel-head">
                     <h3>Toate biletele (fără paginare)</h3>
                     <div className="exam-admin__inline">
                        <button
                           type="button"
                           className="exam-admin__action-btn"
                           onClick={loadTickets}
                           disabled={ticketLoading || createLoading}
                        >
                           Refresh
                        </button>
                     </div>
                  </div>

                  <div className="exam-admin__toolbar">
                     <input
                        className="exam-admin__input"
                        placeholder="Search simplu: id / name / type"
                        value={ticketSearch}
                        onChange={(e) => setTicketSearch(e.target.value)}
                        disabled={ticketLoading || createLoading}
                     />
                  </div>

                  <div className="exam-admin__ticket-list">
                     {filteredTickets.length ? (
                        filteredTickets.map((ticket) => {
                           const active =
                              Number(ticket?.id) === Number(selectedTicket?.id);

                           return (
                              <div
                                 key={ticket.localId}
                                 className={`exam-admin__ticket-item${
                                    active
                                       ? " exam-admin__ticket-item--active"
                                       : ""
                                 }`}
                              >
                                 <button
                                    type="button"
                                    className="exam-admin__ticket-main"
                                    onClick={() =>
                                       setSelectedTicketId(ticket.id)
                                    }
                                    disabled={createLoading}
                                 >
                                    <div className="exam-admin__ticket-top">
                                       <strong>#{ticket.id}</strong>
                                       <span className="exam-admin__badge">
                                          {ticket.questionCount} q
                                       </span>
                                    </div>
                                    <p>{ticket.name || "(no name)"}</p>
                                    <small>{ticket.type || "EXAM"}</small>
                                 </button>
                              </div>
                           );
                        })
                     ) : (
                        <div className="exam-admin__preview-empty">
                           {ticketLoading
                              ? "Loading tickets..."
                              : "No tickets found."}
                        </div>
                     )}
                  </div>
               </article>

               <article className="exam-admin__panel exam-admin__panel--import">
                  <div className="exam-admin__panel-head">
                     <h3>Import JSON + Sync bilet selectat</h3>
                  </div>

                  <div className="exam-admin__import-info">
                     <strong>Bilet selectat:</strong>{" "}
                     {selectedTicket?.id
                        ? `#${selectedTicket.id} - ${selectedTicket.name}`
                        : "nimic selectat"}
                  </div>

                  <div className="exam-admin__import-topbar">
                     <label className="exam-admin__field">
                        <span>Fișier JSON</span>
                        <input
                           className="exam-admin__input"
                           type="file"
                           accept=".json,application/json"
                           onChange={handleImportFileChange}
                           disabled={createLoading}
                        />
                     </label>

                     <p className="exam-admin__muted">
                        File: <strong>{importFileName || "-"}</strong>
                     </p>
                     <p className="exam-admin__muted">
                        Întrebări importate:{" "}
                        <strong>{importItems.length}</strong>
                     </p>
                     <p className="exam-admin__muted">
                        Ticket no pentru imagini:{" "}
                        <strong>{ticketNoForImages || "-"}</strong>
                     </p>
                  </div>

                  <div className="exam-admin__actions">
                     <button
                        type="button"
                        className="exam-admin__primary-btn"
                        onClick={handleCreateAllAndSync}
                        disabled={
                           createLoading ||
                           !selectedTicket?.id ||
                           !importItems.length
                        }
                     >
                        {createLoading
                           ? "Creating + syncing..."
                           : "Create toate + Sync exact ce s-a creat"}
                     </button>
                  </div>

                  <div className="exam-admin__stats-grid">
                     <div className="exam-admin__stat">
                        <div className="exam-admin__stat-value">
                           {createdNowIds.length}
                        </div>
                        <div className="exam-admin__stat-label">
                           Created now
                        </div>
                     </div>
                     <div className="exam-admin__stat">
                        <div className="exam-admin__stat-value">
                           {syncedQuestionIds.length}
                        </div>
                        <div className="exam-admin__stat-label">
                           Synced exact
                        </div>
                     </div>
                     <div className="exam-admin__stat">
                        <div className="exam-admin__stat-value">
                           {createFailures.length}
                        </div>
                        <div className="exam-admin__stat-label">Failures</div>
                     </div>
                  </div>

                  {createFailures.length ? (
                     <details className="ticket-import__failures" open>
                        <summary>
                           Failed questions:{" "}
                           <strong>{createFailures.length}</strong>
                        </summary>
                        <div className="ticket-import__failures-list">
                           {createFailures.map((failure, idx) => (
                              <div key={`create-failure-${idx}`}>
                                 #{failure?.order ?? "?"}: {failure?.message}
                              </div>
                           ))}
                        </div>
                     </details>
                  ) : null}
               </article>
            </div>

            <article className="exam-admin__panel">
               <div className="exam-admin__panel-head">
                  <h3>Extracted + Payload per Order</h3>
               </div>

               <div className="exam-admin__list">
                  {debugPreview.length ? (
                     debugPreview.map((item, idx) => (
                        <div
                           key={`payload-preview-${item?.order ?? idx}`}
                           className="exam-admin__list-item"
                        >
                           <div className="exam-admin__list-item-top">
                              <strong>Order {item?.order}</strong>
                           </div>

                           <p>
                              <strong>Extracted</strong>
                           </p>
                           <pre>{JSON.stringify(item?.extracted, null, 2)}</pre>

                           <p>
                              <strong>Payload</strong>
                           </p>
                           <pre>{JSON.stringify(item?.payload, null, 2)}</pre>
                        </div>
                     ))
                  ) : (
                     <div className="exam-admin__preview-empty">
                        Importă un fișier JSON pentru preview.
                     </div>
                  )}
               </div>
            </article>
         </div>
      </section>
   );
}
